import { useState, useEffect, useCallback } from 'react';
import { vehicleService } from '../../services/api';
import { toast } from '../../utils/toast';
import './AdminTable.css';

const VehiclesAdmin = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await vehicleService.getAll();
      setVehicles(response.data.data || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este vehículo?')) return;

    try {
      await vehicleService.delete(id);
      toast.success('Vehículo eliminado exitosamente');
      loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Error al eliminar vehículo');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingVehicle(null);
    setShowForm(true);
  };

  const handleSave = async (vehicleData) => {
    try {
      if (editingVehicle) {
        await vehicleService.update(editingVehicle.id, vehicleData);
        toast.success('Vehículo actualizado exitosamente');
      } else {
        await vehicleService.create(vehicleData);
        toast.success('Vehículo creado exitosamente');
      }
      setShowForm(false);
      setEditingVehicle(null);
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Error al guardar vehículo');
    }
  };

  if (loading) {
    return <div className="admin-loading">Cargando vehículos...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gestión de Vehículos</h2>
        <button className="btn-primary" onClick={handleCreate}>
          + Nuevo Vehículo
        </button>
      </div>

      {showForm && (
        <VehicleForm
          vehicle={editingVehicle}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingVehicle(null);
          }}
        />
      )}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Precio/Día</th>
              <th>Pasajeros</th>
              <th>Transmisión</th>
              <th>Disponible</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map(vehicle => (
              <tr key={vehicle.id}>
                <td>{vehicle.name}</td>
                <td>{vehicle.category}</td>
                <td>$ {vehicle.pricePerDay?.toLocaleString('es-AR')}</td>
                <td>{vehicle.features?.passengers}</td>
                <td>{vehicle.features?.transmission}</td>
                <td>
                  <span className={`status ${vehicle.available ? 'active' : 'inactive'}`}>
                    {vehicle.available ? 'Sí' : 'No'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(vehicle)}>
                      ✏️
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(vehicle.id)}>
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {vehicles.length === 0 && (
          <div className="empty-state">
            No hay vehículos registrados. Crea uno nuevo para comenzar.
          </div>
        )}
      </div>
    </div>
  );
};

const VehicleForm = ({ vehicle, onSave, onCancel }) => {
  const [formData, setFormData] = useState(vehicle || {
    name: '',
    category: '',
    pricePerDay: 0,
    originalPricePerDay: 0,
    image: '',
    available: true,
    features: {
      passengers: 5,
      doors: 4,
      transmission: 'Manual',
      airConditioning: true,
      luggage: 2,
      unlimited: true
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="form-modal">
      <div className="form-content">
        <h3>{vehicle ? 'Editar Vehículo' : 'Nuevo Vehículo'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Categoría *</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Precio por Día *</label>
              <input
                type="number"
                value={formData.pricePerDay}
                onChange={(e) => setFormData({ ...formData, pricePerDay: parseFloat(e.target.value) })}
                required
              />
            </div>

            <div className="form-group">
              <label>Precio Original</label>
              <input
                type="number"
                value={formData.originalPricePerDay}
                onChange={(e) => setFormData({ ...formData, originalPricePerDay: parseFloat(e.target.value) })}
              />
            </div>

            <div className="form-group full-width">
              <label>URL de Imagen</label>
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Pasajeros</label>
              <input
                type="number"
                value={formData.features?.passengers}
                onChange={(e) => setFormData({
                  ...formData,
                  features: { ...formData.features, passengers: parseInt(e.target.value) }
                })}
              />
            </div>

            <div className="form-group">
              <label>Puertas</label>
              <input
                type="number"
                value={formData.features?.doors}
                onChange={(e) => setFormData({
                  ...formData,
                  features: { ...formData.features, doors: parseInt(e.target.value) }
                })}
              />
            </div>

            <div className="form-group">
              <label>Transmisión</label>
              <select
                value={formData.features?.transmission}
                onChange={(e) => setFormData({
                  ...formData,
                  features: { ...formData.features, transmission: e.target.value }
                })}
              >
                <option value="Manual">Manual</option>
                <option value="Aut.">Automática</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                />
                <span>Disponible</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehiclesAdmin;
